import React, { useEffect, useState } from 'react';
import { usePrevious, useDebounce, useUnmount } from 'react-use';
import store from '../redux/store';
import config from '../config';
import languages from '../constants/DefaultLang';
import { getPages, resetPages } from '../redux/modules/generalPage/action';
import setEnableTealiumStatus from '../redux/modules/enableTealium/action';
import setEnableAdGlareStatus from '../redux/modules/enableAdGlare/action';

const consoleColor = {
  style1: 'color: #bada55;',
  'style1.1': 'color: #bada55; font-weight: bold; font-size: 30px;',
  style2: 'color: #fb5004;',
  'style2.1': 'color: #fb5004; font-weight: bold; font-size: 30px;',
  style3: 'color: #ca8d20;',
  'style3.1': 'color: #ca8d20; font-weight: bold; font-size: 30px;',
};

const useNotFound = (page) => {
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setNotFound(page.error);
  }, [page.error]);
  return notFound;
};

const useDelayJsLoadiing = (page) => {
  const prevPageLoading = usePrevious(page.isLoading);
  const cmsPageApiLoaded =
    prevPageLoading === true && page.isLoading === false && page.error === false;

  // const { data = {} } = page || {};
  // const content = ((data || {}).content || {}).content || {};
  const { data = [] } = page || {};
  const content = Array.isArray(data) && data.length > 0 ? data[0] : {};
  const { js_script_block = '', extra_head = '' } = content;
  const [jsLoading, setJsLoading] = React.useState(false);

  useEffect(() => {
    if (cmsPageApiLoaded) {
      if (js_script_block || extra_head) {
        setJsLoading(true);
        setTimeout(() => {
          setJsLoading(false);
          console.log('setJsLoading false');
        }, 1000);
      }
    }
  }, [cmsPageApiLoaded]);

  return jsLoading;
};

const useDebounceLoading = (page) => {
  const [debounceLoading, setDebounceLoading] = React.useState(true);
  useDebounce(
    () => {
      setDebounceLoading(page.isLoading);
    },
    config.debounceFetchTimeMS,
    [page.isLoading],
  );
  return debounceLoading;
};

const useTealium = (locale, siteConfig, page, isAllowCookies, isEnableTealium, domTitle) => {
  const siteConfigData = siteConfig.data || {};
  const { fairYear = '' } = siteConfigData;
  // const { data = {} } = page || {};
  // const { id = '' } = data;
  // const content = ((data || {}).content || {}).content || {};
  const { data = [], CUSTOM_PAGE = {} } = page || {};
  const content = Array.isArray(data) && data.length > 0 ? data[0] : {};
  const { id = '' } = content;
  const { mtConfig = {} } = content;
  const { tealium_subsection = '', tealium_pagename = '' } = mtConfig;

  const [localeReady, setLocaleReady] = useState(false);
  const [domTitleReady, setDomTitleReady] = useState(false);

  const fireTealiumAction = () => {
    const tealiumSubsection = (tealium_subsection || config.tealium.subsection).toUpperCase();

    // handle 3 page types now: home, press release, others
    // Press release type no fair fair_year
    // Press Release type needs to have article ID and article title

    let pageNameObj;
    let fairYearObj;
    const isPressRelease =
      tealium_subsection.toLowerCase() === config.tealiumPressRelease.subsection.toLowerCase();
    const isPromotion =
      tealium_subsection.toLowerCase() === config.tealiumPromotion.subsection.toLowerCase();

    let pageType;
    switch (tealium_subsection.toLowerCase()) {
      case config.tealiumHome.pageType.toLowerCase():
        pageType = config.tealiumHome.pageType;
        break;
      case config.tealiumPressRelease.subsection.toLowerCase():
        pageType = config.tealiumPressRelease.pageType;
        break;
      case config.tealiumPromotion.subsection.toLowerCase():
        pageType = config.tealiumPromotion.pageType;
        break;
      default:
        pageType = config.tealium.pageType;
    }
    let defaultObj = {
      site_subsection: tealiumSubsection,
      page_type: pageType,
    };

    if (isPressRelease || isPromotion) {
      // articleID && article title
      defaultObj = {
        ...defaultObj,
        article_id: id,
        article_title: tealium_pagename,
      };
    }

    if (tealium_pagename !== '') {
      pageNameObj = isPressRelease
        ? { page_name: `Press Release: ${tealium_pagename}` }
        : isPromotion
        ? { page_name: `Promotion - ${tealium_pagename}` }
        : { page_name: tealium_pagename };
    }
    if (fairYear !== '' && !isPressRelease && !isPromotion) {
      fairYearObj = { fair_year: fairYear };
    }

    window.utag.view(Object.assign(defaultObj, fairYearObj, pageNameObj, CUSTOM_PAGE));

    console.log('defaultObj =>');
    console.log(defaultObj.article_id);
    console.log(`defaultObj > article_id => ${defaultObj.article_id}`);
    console.log(`defaultObj > article_title => ${defaultObj.article_title}`);
    console.log(`defaultObj > site_subsection => ${defaultObj.site_subsection}`);
    console.log(`defaultObj > page_type => ${defaultObj.page_type}`);
    console.log(`fairYearObj => ${JSON.stringify(fairYearObj)}`);
    console.log(`pageNameObj => ${JSON.stringify(pageNameObj)}`);
    console.log(`customPageObj => ${JSON.stringify(CUSTOM_PAGE)}`);

    setDomTitleReady(false);
    console.log('====================================');
    console.log('setDomTitleReady false');
    console.log('====================================');

    setLocaleReady(false);
    console.log('====================================');
    console.log('setLocaleReady false');
    console.log('====================================');
  };

  const fireTealium = () => {
    if (window.utag) {
      fireTealiumAction();
    } else {
      console.log('====================================');
      console.log('window utag not ready');
      console.log('====================================');

      let tryCount = 0;
      const waitTealiumLibraryInit = setInterval(() => {
        console.log('waitTealiumLibraryInit.....');
        tryCount++;
        if (window.utag) {
          fireTealiumAction();
          tryCount = 0;
          clearInterval(waitTealiumLibraryInit);
        }
        if (tryCount > config.interval.times) {
          console.log(`TealiumLibrary init Fail within ${config.interval.times} try`);
          tryCount = 0;
          clearInterval(waitTealiumLibraryInit);
        }
      }, config.interval.frequency);
    }
  };

  // const prevDomTitle = usePrevious(domTitle);
  useEffect(() => {
    if (domTitle) {
      setDomTitleReady(true);
      console.log('====================================');
      // console.log(domTitle, prevDomTitle);
      console.log('setDomTitleReady true');
      console.log('====================================');
    }
  }, [domTitle]);

  useEffect(() => {
    if (isAllowCookies && process.env.REACT_APP_TEALIUM_ENABLE === 'true' && !isEnableTealium) {
      console.log('%cTrigger setEnableTealiumStatus', `${consoleColor.style1}`);
      store.dispatch(setEnableTealiumStatus(true));
    }
  }, [isAllowCookies]);

  const prevPageLoading = usePrevious(page.isLoading);
  const cmsPageApiLoaded =
    prevPageLoading === true && page.isLoading === false && page.error === false;
  const [readyTriggerTealium, setReadyTriggerTealium] = useState(false);

  useEffect(() => {
    console.log('Set CMS Page API Ready to true');
    if (cmsPageApiLoaded) {
      setReadyTriggerTealium(true);
      // console.log('Change readyTriggerTealium true');
    } // else it's triggered by true->false
  }, [cmsPageApiLoaded, domTitleReady]);

  useEffect(() => {
    // console.log('%c***********************************************', `${consoleColor.style1}`);
    // console.log(`%cprevPageLoading: ${prevPageLoading}`, `${consoleColor.style1}`);
    // console.log(`%ccmsPageApiLoaded: ${cmsPageApiLoaded}`, `${consoleColor.style1}`);
    // console.log(`%cisEnableTealium: ${isEnableTealium}`, `${consoleColor.style1}`);
    // console.log(`%creadyTriggerTealium: ${readyTriggerTealium}`, `${consoleColor.style1}`);
    // console.log('%c***********************************************', `${consoleColor.style1}`);

    if (domTitleReady) {
      if (readyTriggerTealium && isEnableTealium) {
        // isEnableTealium: ensure cookie ready
        console.log('%cTrigger Tealium', `${consoleColor['style1.1']}`);
        fireTealium();
        setReadyTriggerTealium(false);
      }
    }
  }, [readyTriggerTealium, isEnableTealium, domTitleReady]);

  const prevLocal = usePrevious(locale);
  useEffect(() => {
    if (prevLocal) {
      setLocaleReady(true);
      // console.log('====================================');
      // console.log('LocaleReady true', localeReady);
      // console.log('====================================');

      const prevLocaleTitle = content[prevLocal]?.seo?.seo_title;
      const currentLocaleTitle = content[locale]?.seo?.seo_title;

      console.log(prevLocaleTitle, currentLocaleTitle);
      if (prevLocaleTitle === currentLocaleTitle) {
        setDomTitleReady(true);
      }
    }
  }, [locale]);

  useEffect(() => {
    // console.log('====================================');
    // console.log('domTitleReady change', domTitleReady);
    // console.log('====================================');

    if (localeReady && domTitleReady) {
      if (isEnableTealium) {
        console.log('%cTrigger Tealium local', `${consoleColor['style1.1']}`);
        fireTealium();
      }
    }
  }, [localeReady, domTitleReady]);
};

const usePage = (
  pageName = 'default',
  pageType = 'Page/Static Page',
  conditions = 'AND (displayStatus:true)',
  isStaticPage = false,
) => {
  const prevPageName = usePrevious(pageName);
  useEffect(() => {
    if (prevPageName) {
      store.dispatch(resetPages());
    }
    store.dispatch(getPages(pageName, pageType, conditions, isStaticPage));
  }, [pageName, conditions]); //add conditions fields
};

const useUnmountResetPage = () => {
  useUnmount(() => {
    store.dispatch(resetPages());
  });
};

const initAdGlare = (lang, adGlareID) => {
  adGlareID
    .toString()
    .split(',')
    .map((v) => v.trim())
    .forEach((v) => {
      (function (r, d, u) {
        const s = d.createElement(r);
        s.async = true;
        s.setAttribute('data-cfasync', false);
        u += `&cb=${Date.now()}${Math.random()}`;
        s.src = u;
        const n = d.getElementsByTagName(r)[0];
        n.parentNode.insertBefore(s, n);
      })('script', document, `//a-engine.hktdc.com/?${v}&keywords=${languages[lang].adGlare}`);
    });

  setTimeout(() => {
    console.log(window.AdGlare);
    window.AdGlare && store.dispatch(setEnableAdGlareStatus(true));
  }, 300);
};

const useAdGlare = (locale, siteConfig, page, isEnableAdGlare) => {
  const siteConfigData = siteConfig.data || {};
  const { adGlare_id } = siteConfigData;

  const prevPageLoading = usePrevious(page.isLoading);
  const cmsPageApiLoaded =
    prevPageLoading === true && page.isLoading === false && page.error === false;

  const prevSiteConfigLoading = usePrevious(siteConfig.isLoading);
  const cmsSiteConfigApiLoaded =
    prevSiteConfigLoading === true && siteConfig.isLoading === false && siteConfig.error === false;
  const [readyTriggerAdGlare, setReadyTriggerAdGlare] = useState(false);

  const refreshAdGlares = (adGlareID, params) => {
    adGlareID
      .toString()
      .split(',')
      .map((v) => v.trim())
      .forEach((v) => {
        if (params) {
          window.AdGlare.API.Refresh(v, params);
        } else {
          window.AdGlare.API.Refresh(v);
        }
      });
  };

  useEffect(() => {
    console.log('Set CMS Page API Ready to true');
    if (cmsPageApiLoaded) {
      setReadyTriggerAdGlare(true);
    } // else it's triggered by true->false
  }, [cmsPageApiLoaded]);

  const prevLocale = usePrevious(locale);
  useEffect(() => {
    // console.log('%====================================', `${consoleColor.style2}`);
    // console.log(`%cprevPageLoading: ${prevPageLoading}`, `${consoleColor.style2}`);
    // console.log(`%ccmsPageApiLoaded: ${cmsPageApiLoaded}`, `${consoleColor.style2}`);
    // console.log(`%ccmsSiteConfigApiLoaded: ${cmsSiteConfigApiLoaded}`, `${consoleColor.style2}`);
    // console.log(`%creadyTriggerAdGlare: ${readyTriggerAdGlare}`, `${consoleColor.style2}`);
    // console.log(`%cprevLocale: ${prevLocale}`, `${consoleColor.style2}`);
    // console.log(`%clocale: ${locale}`, `${consoleColor.style2}`);
    // console.log(`%cadGlare_id: ${adGlare_id}`, `${consoleColor.style2}`);
    // console.log('%====================================', `${consoleColor.style2}`);

    if (!locale) return;

    // useEffect triggered by cmsSiteConfigApiLoaded and cmsSiteConfigApiLoaded
    if (readyTriggerAdGlare && adGlare_id) {
      // Triggered by page change
      // adGlare_id would be ready after cmsSiteConfigApiLoaded
      if (!isEnableAdGlare) {
        console.log('%cTrigger AdGlare init', `${consoleColor['style2.1']}`);
        initAdGlare(locale, adGlare_id);
      } else {
        console.log('%cTrigger AdGlare refresh', `${consoleColor['style2.1']}`);
        refreshAdGlares(adGlare_id);
      }
      setReadyTriggerAdGlare(false); // restore the state for next page change
    } else if (isEnableAdGlare && prevLocale && locale !== prevLocale) {
      // WORKAROUND: prevLocale is undefinded when the page is changed.
      // Triggered by locale change
      console.log('%cTrigger AdGlare locale change', `${consoleColor['style2.1']}`);
      refreshAdGlares(adGlare_id, { keywords: languages[locale].adGlare });
    } // else AdGlare is not ready to be loaded / refreshed

    // todo: cmsPageApiLoaded may change to readyTriggerAdGlare
  }, [locale, cmsPageApiLoaded, cmsSiteConfigApiLoaded]);
};

const useURLChangeCallback = (
  locale,
  data,
  customDetailCallback = null,
  customWatchArray = [],
  isScriptReady = false,
) => {
  const onURLStateChangeCallBack = () => {
    window.onUrlChangeCallBack({
      customDetail: customDetailCallback ? customDetailCallback() : null,
      lang: locale,
      data,
    });
  };

  useEffect(() => {
    if (!(Object.keys(data).length === 0)) {
      if (isScriptReady && window.onUrlChangeCallBack) {
        onURLStateChangeCallBack();
      }
    }
  }, [...customWatchArray, data, locale, isScriptReady]);
};

export {
  useNotFound,
  useDebounceLoading,
  useDelayJsLoadiing,
  usePage,
  useTealium,
  useUnmountResetPage,
  useAdGlare,
  useURLChangeCallback,
};
